'use client';

import React from 'react';
import { ResponsiveModal } from '@west-tennessee-consulting/ui/components/responsive-modal';
import { Star } from 'lucide-react';
import type { ChatSummary } from '../../../types/index.js';

interface ChatDetailsModalProps {
  chat: ChatSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isStarred: boolean;
  projectName?: string | null;
}

function formatAbsolute(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
}

function DetailRow({ label, children }: DetailRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Chat "Details" modal — the full metadata for a single conversation, opened
 * from the sidebar row's kebab / right-click menu.
 */
export function ChatDetailsModal({
  chat,
  open,
  onOpenChange,
  isStarred,
  projectName,
}: ChatDetailsModalProps) {
  if (!chat) return null;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={chat.title}
      description="Conversation details"
      maxWidth="md"
    >
      <dl className="divide-y divide-border">
        <DetailRow label="Messages">{chat.messageCount}</DetailRow>
        <DetailRow label="Status">
          <span className="capitalize">{chat.status}</span>
        </DetailRow>
        <DetailRow label="Created">{formatAbsolute(chat.createdAt)}</DetailRow>
        <DetailRow label="Last message">{formatAbsolute(chat.lastMessageAt)}</DetailRow>
        <DetailRow label="Last updated">{formatAbsolute(chat.updatedAt)}</DetailRow>
        <DetailRow label="Project">{projectName ? projectName : 'None'}</DetailRow>
        <DetailRow label="Starred">
          {isStarred ? (
            <span className="inline-flex items-center gap-1 text-primary">
              <Star className="h-3.5 w-3.5 fill-current" aria-hidden />
              Yes
            </span>
          ) : (
            'No'
          )}
        </DetailRow>
      </dl>
    </ResponsiveModal>
  );
}
